import { TemperatureLocationsLookup } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { HeightUnitConverter } from "@/Common/Utilities/Units/HeightUnitConverter";
import { LabConcentrationUnitConverter } from "@/Common/Utilities/Units/LabConcentrationUnitConverter";
import { TemperatureUnitConverter } from "@/Common/Utilities/Units/TemperatureUnitConverter";
import { BloodGlucoseConcentrationUnit, HeightUnit, SerumCreatinineConcentrationUnit, TemperatureUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { WeightUnitConverter } from "@/Common/Utilities/Units/WeightUnitConverter";
import { MedicalCalculator } from "@/Patient/Helpers/MedicalCalculator";
import { BloodPressure } from "../MedicalSummary/BloodPressure";

enum HeartRateRythm {
    NoneSelected,
    Regular,
    Irregular
}

class VitalSign {
    public date: Date;

    // Raw vital sign values
    public bodyMassIndex: number;
    public bloodPressure: BloodPressure;
    public oxygenSaturation: number;
    public heartRate: number;
    public heartRateRythm: HeartRateRythm = HeartRateRythm.NoneSelected;
    public temperatureLocationLookupId: string;
    public respirationRate: number;
    public waistCircumference: number;
    public creatineClearanceAlgorithmLookupId: string;
    public serumCreatinine: number;
    public creatineClearance: number;
    public bloodGlucose: number;
    public bloodPressureStanding: BloodPressure;
    public bloodPressureLying: BloodPressure;
    public bodySurface: number;
    public note: string;
    public temperature: number;
    public painScaleValue: number;
    public isCommited: boolean;

    protected _weight: number;
    protected _weightUnit: WeightUnit;
    protected _temperatureUnit: TemperatureUnit;
    protected _height: number;
    protected _heightUnit: HeightUnit;
    protected _headCircumference: number;
    protected _headCircumferenceUnits: HeightUnit;
    protected _waistCircumferenceUnits: HeightUnit;
    protected _serumCreatinineConcentrationUnit: SerumCreatinineConcentrationUnit;
    protected _bloodGlucoseUnits: BloodGlucoseConcentrationUnit;

    public constructor(fields?: Partial<VitalSign>) {
        this.setDefault();

        if (fields) {
            this._temperatureUnit = fields.temperatureUnit;

            Object.assign(this, {
                ...fields,
                _weight: fields.weight,
                _weightUnit: fields.weightUnit,
                _height: fields.height,
                _heightUnit: fields.heightUnit,
                _headCircumference: fields.headCircumference,
                _headCircumferenceUnits: fields.headCircumferenceUnits,
                _waistCircumferenceUnits: fields.waistCircumferenceUnits,
                _serumCreatinineConcentrationUnit: fields.serumCreatinineConcentrationUnit,
                _bloodGlucoseUnits: fields.bloodGlucoseUnits
            });
        }
    }

    public get serumCreatinineConcentrationUnit(): SerumCreatinineConcentrationUnit {
        return this._serumCreatinineConcentrationUnit;
    }

    public set serumCreatinineConcentrationUnit(value: SerumCreatinineConcentrationUnit) {
        this.serumCreatinine = LabConcentrationUnitConverter.convertSerumCreatinineConcentrationTo(this._serumCreatinineConcentrationUnit, value, this.serumCreatinine);
        this._serumCreatinineConcentrationUnit = value;
    }

    public get headCircumferenceUnits(): HeightUnit {
        return this._headCircumferenceUnits;
    }

    public get headCircumference(): number {
        return this._headCircumference;
    }

    public set headCircumference(headCircumference: number) {
        this._headCircumference = headCircumference;
    }

    public set headCircumferenceUnits(unit: HeightUnit) {
        if (this._headCircumferenceUnits === unit)
            return;

        this._headCircumference = HeightUnitConverter.convert(this._headCircumferenceUnits, unit, this.headCircumference);
        this._headCircumferenceUnits = unit;
    }

    public getHeadCircumferenceIn(unit: HeightUnit): number {
        if (!this.headCircumference || this._headCircumferenceUnits === unit)
            return this._headCircumferenceUnits;

        return HeightUnitConverter.convert(this.headCircumference, unit, this._headCircumferenceUnits);
    }

    public get waistCircumferenceUnits(): HeightUnit {
        return this._waistCircumferenceUnits;
    }

    public set waistCircumferenceUnits(unit: HeightUnit) {
        this.waistCircumference = HeightUnitConverter.convert(this._waistCircumferenceUnits, unit, this.waistCircumference);
        this._waistCircumferenceUnits = unit;
    }

    public set weightUnit(unit: WeightUnit) {
        if (this._weightUnit === unit)
            return;

        this._weight = WeightUnitConverter.convert(this._weightUnit, unit, this._weight, 2);
        this._weightUnit = unit;
    }

    public get weightUnit(): WeightUnit {
        return this._weightUnit;
    }

    public get weight(): number {
        return this._weight;
    }

    public set weight(weight: number) {
        this._weight = weight;
        this.bodyMassIndex = MedicalCalculator.calculateBodyMassIndex(this.weightInKgs, this.heightInMeters);
        this.bodySurface = MedicalCalculator.calculateBodySurface(this.weightInKgs, HeightUnitConverter.meterToCentimeter(this.heightInMeters));
    }

    public get weightInKgs(): number {
        return this.getWeightIn(WeightUnit.Kilogram);
    }

    public set weightInKgs(value: number) {
        this._weightUnit = WeightUnit.Kilogram;
        this.weight = value;
    }

    public getWeightIn(unit: WeightUnit, numberOfDecimals?: number): number {
        if (!this._weight || this._weightUnit === unit)
            return this._weight;

        return WeightUnitConverter.convert(this._weightUnit, unit, this._weight, numberOfDecimals);
    }

    public set heightUnit(to: HeightUnit) {
        if (this._heightUnit === to)
            return;

        this._height = HeightUnitConverter.convert(this._heightUnit, to, this._height, 2);
        this._heightUnit = to;
    }

    public get heightUnit(): HeightUnit {
        return this._heightUnit;
    }

    public get height(): number {
        return this._height;
    }

    public set height(height: number) {
        this._height = height;
        this.bodyMassIndex = MedicalCalculator.calculateBodyMassIndex(this.weightInKgs, this.heightInMeters);
        this.bodySurface = MedicalCalculator.calculateBodySurface(this.weightInKgs, HeightUnitConverter.meterToCentimeter(this.heightInMeters));
    }

    public get heightInMeters(): number {
        return this.getHeightIn(HeightUnit.Meter);
    }

    public set heightInMeters(value: number) {
        this._heightUnit = HeightUnit.Meter;
        this.height = value;
    }

    public getHeightIn(unit: HeightUnit): number {
        if (!this._height || this._heightUnit === unit)
            return this._height;

        return HeightUnitConverter.convert(this._heightUnit, unit, this._height);
    }

    public set temperatureUnit(unit: TemperatureUnit) {
        this.temperature = TemperatureUnitConverter.convert(this._temperatureUnit, unit, this.temperature);
        this._temperatureUnit = unit;
    }

    public get temperatureUnit(): TemperatureUnit {
        return this._temperatureUnit;
    }

    public get bloodGlucoseUnits(): BloodGlucoseConcentrationUnit {
        return this._bloodGlucoseUnits;
    }

    public set bloodGlucoseUnits(value: BloodGlucoseConcentrationUnit) {
        this.bloodGlucose = LabConcentrationUnitConverter.convertBloodGlucoseConcentrationTo(this._bloodGlucoseUnits, value, this.bloodGlucose);
        this._bloodGlucoseUnits = value;
    }

    private setDefault(): void {
        this._serumCreatinineConcentrationUnit = SerumCreatinineConcentrationUnit.micromolesPerLitre;
        this._bloodGlucoseUnits = BloodGlucoseConcentrationUnit.millimolesPerLitre;
        this.temperatureLocationLookupId = cacheStore.FindLookupByLookupCode(TemperatureLocationsLookup.Oral, TemperatureLocationsLookup.Code)?.LookupID;

        if (sessionStore.useMetricSystemAsDefault) {
            this._temperatureUnit = TemperatureUnit.Celsius;
            this._heightUnit = this._headCircumferenceUnits = this._waistCircumferenceUnits = HeightUnit.Centimeter;
            this._weightUnit = WeightUnit.Kilogram;
            return;
        }

        this._temperatureUnit = TemperatureUnit.Fahrenheit;
        this._heightUnit = this._headCircumferenceUnits = this._waistCircumferenceUnits = HeightUnit.Inch;
        this._weightUnit = WeightUnit.Pound;
    }
}

export { HeartRateRythm, VitalSign };
